import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "../lib/cn";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-9 items-center justify-center rounded-md bg-neutral-100 p-0.5 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-hidden border border-transparent focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-neutral-950 data-[state=active]:shadow-xs dark:data-[state=active]:bg-neutral-900 dark:data-[state=active]:text-neutral-50",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, tabIndex, ...props }, ref) => (
  // Radix sets tabIndex={0} on the panel by default so keyboard users can
  // focus the panel itself. Because every tab in this app contains its own
  // focusable controls, the panel does not need to be a tab stop — and
  // focusing the whole panel both shows a wrapping focus ring around all
  // the settings and triggers a layout shift when the browser scrolls it
  // into view. Default to tabIndex={-1} so Tab moves into the panel's
  // controls directly; callers can opt back in by passing an explicit
  // tabIndex.
  <TabsPrimitive.Content
    ref={ref}
    tabIndex={tabIndex ?? -1}
    className={cn(
      "mt-2 ring-offset-white focus-visible:outline-hidden focus-visible:ring-0 dark:ring-offset-neutral-950",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// Pill-style tab variants (used for page-level navigation)
const PillTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-auto items-center bg-transparent dark:bg-transparent p-0 gap-2 justify-start rounded-none border-none w-max",
      className,
    )}
    {...props}
  />
));
PillTabsList.displayName = "PillTabsList";

const PillTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "cursor-pointer inline-flex items-center justify-center h-8 px-2 py-1 gap-1 rounded-lg type-body-s-regular whitespace-nowrap flex-shrink-0 bg-transparent data-[state=active]:bg-[#ededed] dark:data-[state=active]:bg-neutral-800 data-[state=inactive]:hover:bg-neutral-50 dark:data-[state=inactive]:hover:bg-neutral-800/50 text-neutral-500 dark:text-neutral-400 data-[state=active]:text-neutral-900 dark:data-[state=active]:text-white hover:text-neutral-900 dark:hover:text-neutral-200 data-[state=active]:shadow-none border border-transparent focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 disabled:opacity-50 disabled:cursor-not-allowed",
      className,
    )}
    {...props}
  />
));
PillTabsTrigger.displayName = "PillTabsTrigger";

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  PillTabsList,
  PillTabsTrigger,
};
